import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import {
  SUBSCRIPTION_STATUS_LABEL,
  SUBSCRIPTION_STATUS_STYLES,
  SUBSCRIPTION_STATUSES,
  SubscriptionListResponse,
  SubscriptionListRow,
  SubscriptionStatus,
} from "../../types/subscriptions";
import Pagination from "../OnboardingCustomers/components/Pagination";
import SubscriptionActions from "./components/SubscriptionActions";

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtCapital = (n: number | null): string => {
  if (n === null) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
};

export default function Subscriptions() {
  const { token } = useAuth();
  const { markets } = useMarket();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<SubscriptionStatus[]>([]);
  const [purchaseDateFrom, setPurchaseDateFrom] = useState("");
  const [purchaseDateTo, setPurchaseDateTo] = useState("");
  const [effectDateFrom, setEffectDateFrom] = useState("");
  const [effectDateTo, setEffectDateTo] = useState("");
  const [renewalDateFrom, setRenewalDateFrom] = useState("");
  const [renewalDateTo, setRenewalDateTo] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [data, setData] = useState<SubscriptionListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    selectedStatuses.forEach((s) => params.append("status[]", s));
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (purchaseDateFrom) params.set("purchaseDateFrom", purchaseDateFrom);
    if (purchaseDateTo) params.set("purchaseDateTo", purchaseDateTo);
    if (effectDateFrom) params.set("effectDateFrom", effectDateFrom);
    if (effectDateTo) params.set("effectDateTo", effectDateTo);
    if (renewalDateFrom) params.set("renewalDateFrom", renewalDateFrom);
    if (renewalDateTo) params.set("renewalDateTo", renewalDateTo);
    params.set("limit", String(pageSize));
    params.set("offset", String(page * pageSize));
    try {
      const response = await request<SubscriptionListResponse>(
        `/api/admin/operations/subscriptions?${params.toString()}`,
        { token, markets },
      );
      setData(response);
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    selectedStatuses,
    purchaseDateFrom,
    purchaseDateTo,
    effectDateFrom,
    effectDateTo,
    renewalDateFrom,
    renewalDateTo,
    page,
    pageSize,
    token,
    markets,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = (s: SubscriptionStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedStatuses([]);
    setPurchaseDateFrom("");
    setPurchaseDateTo("");
    setEffectDateFrom("");
    setEffectDateTo("");
    setRenewalDateFrom("");
    setRenewalDateTo("");
    setPage(0);
  };

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <div style={{ marginBottom: "0.75rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#2A3F54" }}>
          Suscripciones
        </h2>
        <p style={{ margin: "0.25rem 0 0", color: "#73879C", fontSize: ".85rem" }}>
          Listado global de suscripciones — filtrable por status, fechas y
          búsqueda libre.
        </p>
      </div>

      <div
        style={{
          background: "white",
          padding: "0.7rem 0.95rem",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          marginBottom: "0.65rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.55rem",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            alignItems: "center",
          }}
        >
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Buscar por email, nombre, DNI, lead id, póliza o subscription id…"
            style={{ ...inputStyle, flex: "1 1 320px", minWidth: 280 }}
          />
          <button type="button" onClick={clearFilters} style={ghostBtn}>
            Limpiar
          </button>
          <div style={{ marginLeft: "auto", fontSize: ".8rem", color: "#73879C" }}>
            {data ? `${data.rows.length} de ${data.total}` : "—"}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "0.35rem",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span style={pillLabel}>Status</span>
          {SUBSCRIPTION_STATUSES.map((s) => {
            const active = selectedStatuses.includes(s);
            const palette = SUBSCRIPTION_STATUS_STYLES[s];
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                style={{
                  padding: "0.3rem 0.65rem",
                  borderRadius: "0.35rem",
                  fontSize: ".74rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: `1px solid ${active ? palette.fg : "#dee2e6"}`,
                  background: active ? palette.bg : "white",
                  color: active ? palette.fg : "#73879C",
                }}
              >
                {SUBSCRIPTION_STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.55rem" }}>
          <DateRange
            label="Compra"
            from={purchaseDateFrom}
            to={purchaseDateTo}
            onFrom={(v) => {
              setPurchaseDateFrom(v);
              setPage(0);
            }}
            onTo={(v) => {
              setPurchaseDateTo(v);
              setPage(0);
            }}
          />
          <DateRange
            label="Efecto"
            from={effectDateFrom}
            to={effectDateTo}
            onFrom={(v) => {
              setEffectDateFrom(v);
              setPage(0);
            }}
            onTo={(v) => {
              setEffectDateTo(v);
              setPage(0);
            }}
          />
          <DateRange
            label="Renovación"
            from={renewalDateFrom}
            to={renewalDateTo}
            onFrom={(v) => {
              setRenewalDateFrom(v);
              setPage(0);
            }}
            onTo={(v) => {
              setRenewalDateTo(v);
              setPage(0);
            }}
          />
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: "#fde2e2",
            color: "#b32424",
            borderRadius: "0.45rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "hidden",
          opacity: loading ? 0.6 : 1,
          transition: "opacity .15s ease",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
            <thead>
              <tr style={{ background: "#f8f9fb" }}>
                <Th>Status</Th>
                <Th>Lead</Th>
                <Th>Póliza</Th>
                <Th>Market</Th>
                <Th>Carrier</Th>
                <Th style={{ textAlign: "right" }}>Capital</Th>
                <Th>Compra</Th>
                <Th>Efecto</Th>
                <Th>Renovación</Th>
                <Th style={{ textAlign: "right" }}>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {data === null || data.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    style={{ padding: "1rem", color: "#73879C", textAlign: "center" }}
                  >
                    {loading ? "Cargando…" : "Sin resultados"}
                  </td>
                </tr>
              ) : (
                data.rows.map((row) => (
                  <Row key={row.subscriptionId} row={row} onAfterChange={load} />
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && data.total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
}

function Row({
  row,
  onAfterChange,
}: {
  row: SubscriptionListRow;
  onAfterChange: () => void;
}) {
  const palette = SUBSCRIPTION_STATUS_STYLES[
    row.status as SubscriptionStatus
  ] ?? { bg: "#e9ecef", fg: "#495057" };
  return (
    <tr style={{ borderBottom: "1px solid #f1f3f5", background: "white" }}>
      <Td>
        <span
          style={{
            display: "inline-block",
            padding: "0.15rem 0.5rem",
            borderRadius: "999px",
            background: palette.bg,
            color: palette.fg,
            fontSize: ".7rem",
            fontWeight: 700,
            letterSpacing: ".3px",
            whiteSpace: "nowrap",
          }}
        >
          {SUBSCRIPTION_STATUS_LABEL[row.status as SubscriptionStatus] ?? row.status}
        </span>
      </Td>
      <Td>
        {row.leadUlid ? (
          <Link
            to={`/leads/${row.leadUlid}`}
            style={{ color: "#0069d9", textDecoration: "none", fontWeight: 600 }}
          >
            {row.leadEmail ?? row.leadName ?? `Lead ${row.leadId}`}
          </Link>
        ) : (
          row.leadEmail ?? row.leadName ?? "—"
        )}
        <div style={{ fontSize: ".7rem", color: "#73879C" }}>
          {row.leadDni ?? `lead#${row.leadId ?? "—"}`}
        </div>
      </Td>
      <Td mono>{row.policyNumber ?? "—"}</Td>
      <Td mono>{row.leadMarket ?? "—"}</Td>
      <Td>{row.carrier ?? "—"}</Td>
      <Td mono style={{ textAlign: "right" }}>
        {fmtCapital(row.capital)}
      </Td>
      <Td>{fmtDate(row.purchaseDate)}</Td>
      <Td>{fmtDate(row.effectDate)}</Td>
      <Td>{fmtDate(row.renewalDate)}</Td>
      <Td style={{ textAlign: "right" }}>
        <SubscriptionActions row={row} onAfterChange={onAfterChange} />
      </Td>
    </tr>
  );
}

function DateRange({
  label,
  from,
  to,
  onFrom,
  onTo,
}: {
  label: string;
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
      <span style={pillLabel}>{label}</span>
      <input
        type="date"
        value={from}
        onChange={(e) => onFrom(e.target.value)}
        style={{ ...inputStyle, width: 140 }}
      />
      <span style={{ color: "#73879C", fontSize: ".75rem" }}>→</span>
      <input
        type="date"
        value={to}
        onChange={(e) => onTo(e.target.value)}
        style={{ ...inputStyle, width: 140 }}
      />
    </div>
  );
}

function Th({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th
      style={{
        padding: "0.55rem 0.75rem",
        textAlign: "left",
        fontSize: ".7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: "#73879C",
        borderBottom: "1px solid #e9ecef",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "0.55rem 0.75rem",
        fontFamily: mono ? "monospace" : "inherit",
        color: "#1f2933",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".85rem",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.4rem 0.85rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".78rem",
  fontWeight: 600,
  cursor: "pointer",
};

const pillLabel: React.CSSProperties = {
  fontSize: ".72rem",
  color: "#73879C",
  fontWeight: 600,
};
